import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "./RootStore";
import type ServiceStore from "./ServiceStore";

/**
 * NotificationCenterStore (Slice 2)
 *
 * Root-level view store that backs the global notifications bell + inbox.
 * Composes the two Feathers-backed service stores wired in Slice 1:
 *
 *   - notificationsStore       (service: 'notifications')
 *   - notificationReadsStore   (service: 'notification-reads')
 *
 * Both underlying ServiceStores auto-react to Feathers socket events via
 * `addOrUpdateEvent` / `removeEvent` (see ServiceStore.handleEvents), so any
 * `created` / `patched` / `removed` push from the backend mutates the
 * observable `items` arrays. This view store reads those entities through
 * computed getters, which means MobX will recompute `unreadCount` and
 * `recent` automatically whenever a notification or read row changes.
 *
 * Locked design decisions from the spec:
 *   - Recipient model is role-based and enforced server-side via the
 *     `filterByRecipientRoles` hook. We do NOT re-filter by role here.
 *   - severity ∈ {'info','warning','critical'}
 *   - read-status lifecycle is encoded in notification_reads.status:
 *     'read' | 'dismissed' | 'archived'
 *   - UNIQUE(notificationId, userId) on notification_reads — markRead /
 *     dismiss must patch the existing row if one exists, otherwise create.
 */

// ---------- Types ----------

export type NotificationSeverity = "info" | "warning" | "critical";

export type NotificationReadStatus = "read" | "dismissed" | "archived";

/**
 * Local read-state, derived from notification_reads rows for the current user.
 * 'unread' is the implicit default when no read row exists.
 */
export type NotificationViewState = "unread" | "read" | "dismissed" | "archived";

export interface NotificationEntity {
  id: number;
  type: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  sourceTable: string | null;
  sourceId: number | string | null;
  link: string | null;
  recipientRolesJson: string;
  actionType: string | null;
  actionPayloadJson: string | null;
  area: string | null;
  projectId: number | null;
  panelId: number | null;
  initiatedByUserId: number | null;
  initiatedByName: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface NotificationReadEntity {
  id: number;
  notificationId: number;
  userId: number;
  status: NotificationReadStatus;
  readAt: string | null;
  actedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A notification annotated with the current user's read state. This is what
 * the inbox / bell list renders.
 */
export interface AnnotatedNotification extends NotificationEntity {
  viewState: NotificationViewState;
  readRowId: number | null;
  readAt: string | null;
  actedAt: string | null;
}

// ---------- Constants ----------

/** Maximum number of recent notifications surfaced in the feed. */
const RECENT_LIMIT = 50;

/** How many notification_reads rows to hydrate on initial load. */
const READS_HYDRATION_LIMIT = 200;

// ---------- Store ----------

class NotificationCenterStore {
  rootStore: RootStore;

  /** True while the initial hydrate() call is in flight. */
  isHydrating: boolean = false;
  /** True after hydrate() has completed at least once successfully. */
  hasHydrated: boolean = false;
  /** Last error from hydrate / mutation calls — surfaced to UI if set. */
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ===== Underlying service-store accessors =====

  /**
   * Look up a service store by key on the rootStore. Returns null if the
   * store has not been mounted yet (the ServiceStoreManager mounts stores
   * in batches, so during early startup these can be undefined).
   */
  private getServiceStore(storeKey: string): ServiceStore | null {
    const candidate = (this.rootStore as unknown as Record<string, unknown>)[
      storeKey
    ];
    if (!candidate) return null;
    // Duck-type check — ServiceStore instances expose .find / .patch / .create
    const maybe = candidate as Partial<ServiceStore>;
    if (typeof maybe.find !== "function") return null;
    return candidate as ServiceStore;
  }

  private get notificationsStore(): ServiceStore | null {
    return this.getServiceStore("notificationsStore");
  }

  private get notificationReadsStore(): ServiceStore | null {
    return this.getServiceStore("notificationReadsStore");
  }

  /** Resolve the current logged-in userId via the rootStore.authStore. */
  private get currentUserId(): number | null {
    const uid = this.rootStore.authStore?.userId;
    return typeof uid === "number" ? uid : null;
  }

  // ===== Computed feeds =====

  /** All notifications cached locally (already role-filtered server-side). */
  get notifications(): NotificationEntity[] {
    const store = this.notificationsStore;
    if (!store) return [];
    return (store.entities as unknown as NotificationEntity[]) ?? [];
  }

  /** All notification_reads rows cached locally for the current user. */
  get reads(): NotificationReadEntity[] {
    const store = this.notificationReadsStore;
    const uid = this.currentUserId;
    if (!store || uid == null) return [];
    const all = (store.entities as unknown as NotificationReadEntity[]) ?? [];
    return all.filter((r) => r.userId === uid);
  }

  /**
   * Map: notificationId -> the current user's read row (if any).
   * Used to annotate the feed and to find an existing row before creating
   * a new one (honoring the UNIQUE(notificationId, userId) constraint).
   */
  get readsByNotificationId(): Map<number, NotificationReadEntity> {
    const map = new Map<number, NotificationReadEntity>();
    for (const r of this.reads) {
      // If duplicates exist (shouldn't, given the UNIQUE constraint), keep
      // the most recently updated.
      const existing = map.get(r.notificationId);
      if (!existing || (r.updatedAt ?? "") > (existing.updatedAt ?? "")) {
        map.set(r.notificationId, r);
      }
    }
    return map;
  }

  /**
   * Notifications visible to the current user — excludes anything past its
   * expiresAt. Sorted by createdAt desc.
   */
  get visibleNotifications(): NotificationEntity[] {
    const now = Date.now();
    return this.notifications
      .filter((n) => {
        if (!n.expiresAt) return true;
        const exp = Date.parse(n.expiresAt);
        return Number.isNaN(exp) ? true : exp > now;
      })
      .slice()
      .sort((a, b) => {
        const ad = Date.parse(a.createdAt) || 0;
        const bd = Date.parse(b.createdAt) || 0;
        return bd - ad;
      });
  }

  /**
   * Annotate a notification with the current user's read state.
   * 'unread' is implicit when no read row exists.
   */
  private annotate(n: NotificationEntity): AnnotatedNotification {
    const readRow = this.readsByNotificationId.get(n.id);
    if (!readRow) {
      return {
        ...n,
        viewState: "unread",
        readRowId: null,
        readAt: null,
        actedAt: null,
      };
    }
    return {
      ...n,
      viewState: readRow.status as NotificationViewState,
      readRowId: readRow.id,
      readAt: readRow.readAt,
      actedAt: readRow.actedAt,
    };
  }

  /**
   * The feed surfaced to the bell / inbox. Last RECENT_LIMIT visible
   * notifications sorted by createdAt desc, each annotated with the current
   * user's read state.
   */
  get recent(): AnnotatedNotification[] {
    return this.visibleNotifications
      .slice(0, RECENT_LIMIT)
      .map((n) => this.annotate(n));
  }

  /**
   * Number of visible notifications that do NOT have a read row of status
   * 'read' or 'dismissed' for the current user. (Drives the bell badge.)
   */
  get unreadCount(): number {
    if (this.currentUserId == null) return 0;
    const reads = this.readsByNotificationId;
    let count = 0;
    for (const n of this.visibleNotifications) {
      const r = reads.get(n.id);
      if (!r) {
        count += 1;
        continue;
      }
      if (r.status !== "read" && r.status !== "dismissed") count += 1;
    }
    return count;
  }

  /** Convenience: just the unread slice of `recent`. */
  get unreadRecent(): AnnotatedNotification[] {
    return this.recent.filter((n) => n.viewState === "unread");
  }

  // ===== Hydration =====

  /**
   * Initial load. Pulls the last 50 notifications + last 200 read rows for
   * the current user. After this, the ServiceStores keep themselves in sync
   * via Feathers socket events (created/patched/removed) — no polling needed.
   */
  hydrate = async (): Promise<void> => {
    const notif = this.notificationsStore;
    const reads = this.notificationReadsStore;
    if (!notif || !reads) {
      // Stores not mounted yet (early in startup). Caller can retry after
      // ServiceStoreManager finishes batching. We don't throw here — the
      // bell can render with unreadCount=0 until hydration succeeds.
      return;
    }

    runInAction(() => {
      this.isHydrating = true;
      this.error = null;
    });

    try {
      await Promise.all([
        notif.find({
          query: {
            $sort: { createdAt: -1 },
            $limit: RECENT_LIMIT,
          },
        }),
        reads.find({
          query: {
            $sort: { createdAt: -1 },
            $limit: READS_HYDRATION_LIMIT,
            // Server-side `enforceSelfUserId` hook pins this to the caller's
            // userId for external requests — we don't need to set it here.
          },
        }),
      ]);

      runInAction(() => {
        this.hasHydrated = true;
        this.isHydrating = false;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[NotificationCenterStore] hydrate failed:", message);
      runInAction(() => {
        this.error = message;
        this.isHydrating = false;
      });
    }
  };

  // ===== Mutations =====

  /**
   * Find-or-create a notification_reads row for (notificationId, currentUserId)
   * and set it to the given status. Honors the UNIQUE(notificationId, userId)
   * DB constraint by looking up the existing row first.
   */
  private upsertReadStatus = async (
    notificationId: number,
    status: NotificationReadStatus,
    extraFields: Partial<
      Pick<NotificationReadEntity, "readAt" | "actedAt">
    > = {}
  ): Promise<NotificationReadEntity | null> => {
    const reads = this.notificationReadsStore;
    const uid = this.currentUserId;
    if (!reads || uid == null) return null;

    const existing = this.readsByNotificationId.get(notificationId) ?? null;
    const nowIso = new Date().toISOString();

    try {
      if (existing) {
        const patch: Record<string, unknown> = { status };
        if (status === "read" && !existing.readAt) {
          patch.readAt = extraFields.readAt ?? nowIso;
        }
        if (extraFields.actedAt !== undefined) {
          patch.actedAt = extraFields.actedAt;
        }
        const response = (await reads.patch(
          existing.id,
          patch
        )) as unknown as NotificationReadEntity | undefined;
        return response ?? null;
      }

      const createPayload: Record<string, unknown> = {
        notificationId,
        userId: uid,
        status,
      };
      if (status === "read") {
        createPayload.readAt = extraFields.readAt ?? nowIso;
      }
      if (extraFields.actedAt !== undefined) {
        createPayload.actedAt = extraFields.actedAt;
      }
      const response = (await reads.create(
        createPayload
      )) as unknown as NotificationReadEntity | undefined;
      return response ?? null;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(
        "[NotificationCenterStore] upsertReadStatus failed for",
        notificationId,
        status,
        message
      );
      runInAction(() => {
        this.error = message;
      });
      return null;
    }
  };

  /** Mark a single notification as read for the current user. */
  markRead = async (
    notificationId: number
  ): Promise<NotificationReadEntity | null> => {
    return this.upsertReadStatus(notificationId, "read");
  };

  /**
   * Mark all currently-unread visible notifications as read. Issues one
   * upsert per notification (no bulk endpoint exists in Slice 1). Errors
   * on individual items are logged and skipped — successful rows still
   * land via the socket-driven addOrUpdateEvent path.
   */
  markAllRead = async (): Promise<void> => {
    const unread = this.unreadRecent;
    if (unread.length === 0) return;
    await Promise.all(unread.map((n) => this.markRead(n.id).catch(() => null)));
  };

  /** Mark a notification as dismissed for the current user (hides from feed). */
  dismiss = async (
    notificationId: number
  ): Promise<NotificationReadEntity | null> => {
    return this.upsertReadStatus(notificationId, "dismissed");
  };

  /**
   * Record that the user followed the CTA on this notification. Sets
   * actedAt and (if not already set) marks the row as read. Used by the
   * Reissue / "Open piece" / "Open chip" buttons in the inbox.
   */
  markActed = async (
    notificationId: number
  ): Promise<NotificationReadEntity | null> => {
    const nowIso = new Date().toISOString();
    return this.upsertReadStatus(notificationId, "read", { actedAt: nowIso });
  };

  // ===== Lookups =====

  /** Find an annotated notification by id (uses the visible/recent feed). */
  getById(notificationId: number): AnnotatedNotification | undefined {
    return this.recent.find((n) => n.id === notificationId);
  }
}

export default NotificationCenterStore;
